import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import EmailImageUpload from "../EmailImageUpload";
import { getImageUrl } from "../../../utils/imageHelper";

/**
 * 3Col_1Img_center Component
 * Three-column layout with:
 * - Left column: 2 content blocks with icons and text
 * - Center column: Large image
 * - Right column: 2 content blocks with icons and text
 * - Title at top
 * - Bottom text below image
 */
const ThreeColOneImgCenter = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    bottomText = "",
    centerImage = null,
    leftContent = [],
    rightContent = [],
    component_name = "3Col_1Img_center",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localBottomText, setLocalBottomText] = useState(bottomText);
  const [localCenterImage, setLocalCenterImage] = useState(centerImage);
  const [localLeftContent, setLocalLeftContent] = useState(
    Array.isArray(leftContent) && leftContent.length > 0
      ? leftContent
      : [
          {
            icon: null,
            text: "Vivamus dolor elementum id odio sed, posuere consequat justo. Maecenas rhoncus pulvinar",
          },
          {
            icon: null,
            text: "Vivamus dolor elementum id odio sed, posuere consequat justo. Maecenas rhoncus pulvinar",
          },
        ]
  );
  const [localRightContent, setLocalRightContent] = useState(
    Array.isArray(rightContent) && rightContent.length > 0
      ? rightContent
      : [
          {
            icon: null,
            text: "Vivamus dolor elementum id odio sed, posuere consequat justo. Maecenas rhoncus pulvinar",
          },
          {
            icon: null,
            text: "Vivamus dolor elementum id odio sed, posuere consequat justo. Maecenas rhoncus pulvinar",
          },
        ]
  );

  useEffect(() => {
    setLocalTitle(title || "");
    setLocalBottomText(bottomText || "");
    setLocalCenterImage(centerImage);
    if (Array.isArray(leftContent) && leftContent.length > 0) {
      setLocalLeftContent(leftContent);
    }
    if (Array.isArray(rightContent) && rightContent.length > 0) {
      setLocalRightContent(rightContent);
    }
  }, [id, title, bottomText, centerImage, leftContent, rightContent]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      bottomText: localBottomText,
      centerImage: localCenterImage,
      leftContent: localLeftContent,
      rightContent: localRightContent,
    });
    setIsEditing(false);
  };

  const handleContentUpdate = (side, index, field, value) => {
    if (side === "left") {
      const updated = [...localLeftContent];
      updated[index] = { ...updated[index], [field]: value };
      setLocalLeftContent(updated);
    } else {
      const updated = [...localRightContent];
      updated[index] = { ...updated[index], [field]: value };
      setLocalRightContent(updated);
    }
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component three-col-img-center-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Three Column Center Image Layout</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Title</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter title"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Center Image</label>
          <EmailImageUpload
            initialImage={localCenterImage}
            onImageUpload={setLocalCenterImage}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Bottom Text</label>
          <input
            className="form-control"
            value={localBottomText}
            onChange={(e) => setLocalBottomText(e.target.value)}
            placeholder="Enter bottom text"
          />
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Left Content Blocks</label>
            {localLeftContent.map((item, index) => (
              <div
                key={index}
                className="border rounded p-3 mb-3"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Block {index + 1}</strong>
                </div>
                <div className="mb-2">
                  <label className="form-label small">Icon</label>
                  <EmailImageUpload
                    initialImage={item.icon}
                    onImageUpload={(img) =>
                      handleContentUpdate("left", index, "icon", img)
                    }
                  />
                </div>
                <div>
                  <label className="form-label small">Text</label>
                  <textarea
                    className="form-control form-control-sm"
                    value={item.text || ""}
                    onChange={(e) =>
                      handleContentUpdate("left", index, "text", e.target.value)
                    }
                    placeholder="Enter text"
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Right Content Blocks</label>
            {localRightContent.map((item, index) => (
              <div
                key={index}
                className="border rounded p-3 mb-3"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Block {index + 1}</strong>
                </div>
                <div className="mb-2">
                  <label className="form-label small">Icon</label>
                  <EmailImageUpload
                    initialImage={item.icon}
                    onImageUpload={(img) =>
                      handleContentUpdate("right", index, "icon", img)
                    }
                  />
                </div>
                <div>
                  <label className="form-label small">Text</label>
                  <textarea
                    className="form-control form-control-sm"
                    value={item.text || ""}
                    onChange={(e) =>
                      handleContentUpdate("right", index, "text", e.target.value)
                    }
                    placeholder="Enter text"
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  const centerImageUrl = getImageUrl(localCenterImage);

  return (
    <>
      {!previewMode && (
        <div className="email-component three-col-img-center mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tr>
          <td
            align="center"
            style={{
              padding: "32px 16px",
              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
            }}
          >
            {/* Title */}
            {localTitle && (
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "bold",
                  color: primaryColor || "#333333",
                  marginBottom: "32px",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                {localTitle}
              </div>
            )}

            {/* Three Column Layout */}
            <table
              width="100%"
              cellPadding="0"
              cellSpacing="0"
              border="0"
              style={{
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              <tr>
                {/* Left Column */}
                <td
                  width="25%"
                  valign="top"
                  style={{
                    padding: "0 8px",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                  }}
                >
                  {localLeftContent.map((item, index) => {
                    const iconUrl = getImageUrl(item.icon);
                    return (
                      <div
                        key={index}
                        style={{
                          marginBottom: index < localLeftContent.length - 1 ? "24px" : "0",
                        }}
                      >
                        <table
                          width="100%"
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tr>
                            <td
                              width="60"
                              valign="top"
                              style={{
                                paddingRight: "12px",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <table
                                cellPadding="0"
                                cellSpacing="0"
                                border="0"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor: iconUrl ? "transparent" : "#ffe5e5",
                                  border: "2px solid #ff6b6b",
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                    }}
                                  >
                                    {iconUrl ? (
                                      <img
                                        src={iconUrl}
                                        alt="Icon"
                                        width="36"
                                        height="36"
                                        style={{
                                          width: "36px",
                                          height: "36px",
                                          display: "block",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          backgroundColor: "#ff6b6b",
                                          display: "block",
                                        }}
                                      />
                                    )}
                                  </td>
                                </tr>
                              </table>
                            </td>
                            <td
                              valign="top"
                              style={{
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: primaryColor || "#333333",
                                  lineHeight: "1.6",
                                  margin: "0",
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                {item.text || ""}
                              </p>
                            </td>
                          </tr>
                        </table>
                      </div>
                    );
                  })}
                </td>

                {/* Center Column - Image */}
                <td
                  width="50%"
                  valign="middle"
                  align="center"
                  style={{
                    padding: "0 16px",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                  }}
                >
                  <table
                    width="100%"
                    cellPadding="0"
                    cellSpacing="0"
                    border="0"
                    align="center"
                    style={{
                      maxWidth: "400px",
                      border: "2px solid #ff6b6b",
                      backgroundColor: "#ffe5e5",
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    }}
                  >
                    <tr>
                      <td
                        align="center"
                        valign="middle"
                        style={{
                          padding: "20px",
                          minHeight: "300px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        {centerImageUrl ? (
                          <img
                            src={centerImageUrl}
                            alt={localTitle || "Center Image"}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              display: "block",
                            }}
                          />
                        ) : (
                          <img
                            src="/img-preview.svg"
                            alt="Image placeholder"
                            width="100"
                            height="100"
                            style={{
                              width: "100px",
                              height: "100px",
                              display: "block",
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  </table>
                </td>

                {/* Right Column */}
                <td
                  width="25%"
                  valign="top"
                  style={{
                    padding: "0 8px",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                  }}
                >
                  {localRightContent.map((item, index) => {
                    const iconUrl = getImageUrl(item.icon);
                    return (
                      <div
                        key={index}
                        style={{
                          marginBottom: index < localRightContent.length - 1 ? "24px" : "0",
                        }}
                      >
                        <table
                          width="100%"
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tr>
                            <td
                              width="60"
                              valign="top"
                              style={{
                                paddingRight: "12px",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <table
                                cellPadding="0"
                                cellSpacing="0"
                                border="0"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor: iconUrl ? "transparent" : "#ffe5e5",
                                  border: "2px solid #ff6b6b",
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                    }}
                                  >
                                    {iconUrl ? (
                                      <img
                                        src={iconUrl}
                                        alt="Icon"
                                        width="36"
                                        height="36"
                                        style={{
                                          width: "36px",
                                          height: "36px",
                                          display: "block",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          backgroundColor: "#ff6b6b",
                                          display: "block",
                                        }}
                                      />
                                    )}
                                  </td>
                                </tr>
                              </table>
                            </td>
                            <td
                              valign="top"
                              style={{
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: primaryColor || "#333333",
                                  lineHeight: "1.6",
                                  margin: "0",
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                {item.text || ""}
                              </p>
                            </td>
                          </tr>
                        </table>
                      </div>
                    );
                  })}
                </td>
              </tr>
            </table>

            {/* Bottom Text */}
            {localBottomText && (
              <div
                style={{
                  fontSize: "14px",
                  color: primaryColor || "#666666",
                  marginTop: "24px",
                  textAlign: "center",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                {localBottomText}
              </div>
            )}
          </td>
        </tr>
      </table>
    </>
  );
};

export default ThreeColOneImgCenter;

